/**
 * Buttons & Effects drawer. The Design tab's combined drawer
 * collects two adjacent subpanels (`_refreshButtonsSection` +
 * `_refreshEffectsSection` per the plan) under one drilldown.
 *
 * See colors-drawer.js for the Phase B.1 adapter rationale.
 */

import { eventBus } from '../../core/events.js';
import { renderEntranceGroup } from './entrance-group.js';

/**
 * @param {HTMLElement} bodyEl
 * @param {{ panel: import('../property-panel.js').PropertyPanel }} ctx
 */
export function renderLayoutContent(bodyEl, { panel }) {
  if (!bodyEl || !panel) return () => {};

  // Each subpanel's `_renderXSubpanel` does `scope.innerHTML = …` and wipes
  // whatever was in `scope`. Give Buttons + Effects their own child containers
  // so they coexist inside the combined drawer. Both render methods also call
  // their own `_wireXSubpanel` internally — don't double-wire.
  bodyEl.innerHTML = `
    <div data-layout-buttons></div>
    <div class="pb-layout-divider" role="separator" aria-hidden="true"></div>
    <div data-layout-effects></div>
    <div class="pb-layout-divider" role="separator" aria-hidden="true"></div>
    <div data-layout-entrance></div>
  `;
  const buttonsScope = bodyEl.querySelector('[data-layout-buttons]');
  const effectsScope = bodyEl.querySelector('[data-layout-effects]');
  const entranceScope = bodyEl.querySelector('[data-layout-entrance]');

  panel._renderButtonsSubpanel(buttonsScope);
  panel._renderEffectsSubpanel(effectsScope);
  // EA-4. Owns its own `state-changed` subscription — the template block
  // appears and disappears with the toggle, so it rebuilds rather than patching.
  const teardownEntrance = renderEntranceGroup(entranceScope);

  // State-changed updates route through the in-place _refresh* methods so
  // the live preview button + form-effect inputs don't get detached mid-edit.
  const refresh = () => {
    panel._refreshButtonsSection(buttonsScope);
    panel._refreshEffectsSection(effectsScope);
  };
  const off = eventBus.on('state-changed', refresh);

  return () => {
    off?.();
    teardownEntrance?.();
  };
}
